import classnames from 'classnames'
import omit from 'omit.js'
import TextArea from 'rc-textarea'
import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback, useLayoutEffect } from 'react'
import Button from '../../wui-button/src'
import { WebUI, prefix as componentPrefix, getChildrenText, getNid, setComponentClass, stopKeyEventBubbles } from '../../wui-core/src/index'
import Icon from '../../wui-icon/src'
import { DefaultProps, InputProps, InputWithDefaultProps } from './iInput'
import { useConfigContext } from "../../wui-provider/src/context";

const textareaPrefix = componentPrefix + '-textarea'

// 只读规范更改：不支持onFocus事件、hover和选中时没有额外视觉效果、无法通过tab按键选中
const defaultProps: DefaultProps = {
    componentClass: 'input',
    clsPrefix: 'wui-input',
    type: 'text',
    size: 'md',
    bordered: true,
    trim: false,
    trigger: 'click',
    iconRender: () => null,
    allowInputOverMax: false, // textarea默认行为调整为不允许超出maxLength
    visibilityToggle: true,
    debounceDelay: 0,
    antd: false
}

let cutValue = (value?: string | number, maxLength?: number) => {
    if (maxLength && value) {
        value = value.toString().substring(0, maxLength)
    }
    return value ?? ''
}

const Input = forwardRef<any, InputProps>((props, ref) => {
    // 使用 useConfigContext 获取全局配置
    const configContext = useConfigContext({ name: "input" });
    const propsWithDefaults = { ...defaultProps, ...configContext, ...props } as InputWithDefaultProps

    const {
        value: propsValue,
        defaultValue,
        maxLength,
        passwordVisible: propsPasswordVisible,
        type,
        componentClass,
        antd,
        onCompositionStart,
        onCompositionEnd,
        disabled,
        allowClear,
        trim,
        onFocus,
        onClick,
        onKeyDown,
        ignoreStopKey,
        loading,
        className,
        size,
        align,
        clsPrefix,
        bordered,
        placeholder,
        debounceDelay,
        readOnly,
        requiredStyle,
        onChange,
        onSearch,
        onBlur,
        onPressEnter,
        focusSelect,
        prefix,
        suffix,
        innerClassName,
        id,
        fieldid,
        showMaxLabel,
        allowInputOverMax,
        title,
        autoSize: propsAutoSize,
        browser,
        visibilityToggle,
        trigger,
        iconRender,
        autoFocus,
        onLinkClick
    } = propsWithDefaults

    const initialValue = propsValue === undefined
        ? cutValue(defaultValue, maxLength)
        : cutValue(propsValue, maxLength)

    const [passwordVisible, setPasswordVisible] = useState(propsPasswordVisible ?? false)
    const [value, setValue] = useState(initialValue)
    const [focused, setFocused] = useState(false)
    const [prevValue, setPrevValue] = useState(propsValue)
    const [autoSize, setAutoSize] = useState(
        [type, componentClass].includes('textarea') ? getAutoSize(propsWithDefaults) : undefined
    )

    const inputRef = useRef<any>(null)
    const clickClearBtnRef = useRef(false)
    const clickSearchBtnRef = useRef(false)
    const lastScrollCallRef = useRef(0)
    const eRef = useRef<any>(null)
    const isInputChineseRef = useRef(false)
    const suffixRef = useRef<HTMLSpanElement>(null)
    const [suffixWidth, setSuffixWidth] = useState(0)

    function getAutoSize(props: InputProps) {
        const { defaultValue, value, type, componentClass, autoSize, placeholder } = props
        if (![type, componentClass].includes('textarea')) return
        if ((typeof autoSize !== 'object') || autoSize?.minRows || placeholder || value || defaultValue || prevValue) {
            return autoSize
        }
    }

    useEffect(() => {
        if (type === 'password') {
            removePasswordValue()
        }
    }, [type])

    useEffect(() => {
        // 实现autoFocus功能，这是为了解决在某些情况下，input框聚焦失败
        // 首先使用input原生属性聚焦，这种方法性能更好
        // 但特殊情况下，原生属性无法聚焦，则使用编程式聚焦再次尝试，这种方法有一定延迟但更可靠
        if (autoFocus) {
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 300) // 延迟确保DOM完全渲染
            return () => clearTimeout(timer)
        }
    }, []) // 只在组件挂载时执行一次

    useEffect(() => {
        if (isInputChineseRef.current === false) {
            if (propsValue !== undefined || prevValue !== propsValue) {
                setValue(propsValue ?? '')
            }
        } else if (propsValue !== undefined && prevValue !== propsValue && value !== propsValue) {
            isInputChineseRef.current = false
        }
        if (propsPasswordVisible !== undefined) {
            setPasswordVisible(propsPasswordVisible)
        }
        const newAutoSize = [type, componentClass].includes('textarea') ? getAutoSize(propsWithDefaults) : undefined
        setAutoSize(newAutoSize)
        setPrevValue(propsValue)
    }, [propsValue, propsPasswordVisible, type, componentClass, prevValue, value])

    // 监听 suffix 宽度变化
    useLayoutEffect(() => {
        // 只在 textarea 且有 suffix 和 allowClear 时计算
        if ((type === 'textarea' || componentClass === 'textarea') && suffix && allowClear && suffixRef.current) {
            const updateSuffixWidth = () => {
                const width = suffixRef.current?.offsetWidth || 0
                setSuffixWidth(width)
            }

            // 初始计算
            updateSuffixWidth()

            // 使用 ResizeObserver 监听尺寸变化
            const resizeObserver = new ResizeObserver(updateSuffixWidth)
            resizeObserver.observe(suffixRef.current)

            return () => {
                resizeObserver.disconnect()
            }
        }
    }, [type, componentClass, suffix, allowClear])

    // Methods
    const removePasswordValue = useCallback(() => {
        if (inputRef.current && inputRef.current.getAttribute('type') === 'password' && inputRef.current.hasAttribute('value')) {
            inputRef.current.removeAttribute('value')
        }
    }, [])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>, valueChinese: InputWithDefaultProps['value'], composition?: 'start' | 'end') => {
        const now = new Date().getTime()
        if (now - lastScrollCallRef.current < debounceDelay) return
        lastScrollCallRef.current = now
        // const value = cutValue(this.input.value || e.target.value, maxLength)
        // #QDJCJS-7720设置maxLength后搜狗输入法录入中文过程中，当录入快到达最大长度时，会删已经录入的文本
        const newValue = valueChinese || inputRef.current?.value || e.target.value
        if (propsValue === undefined || isInputChineseRef.current || composition === 'end') {
            setValue(newValue ?? '')
        }
        if (isInputChineseRef.current) {
            return
        }
        antd ? onChange?.(e) : onChange?.(newValue, e)
    }

    const handleTextareaChange = (e: React.ChangeEvent<HTMLInputElement>, valueChinese: InputWithDefaultProps['value'], composition?: 'start' | 'end') => {
        const now = new Date().getTime()
        if (now - lastScrollCallRef.current < debounceDelay) return
        lastScrollCallRef.current = now

        // 超长处理
        let limitValue = (valueChinese || e.target.value) + ''
        if (Number(e.target.selectionStart) >= limitValue.length && e.target.scrollHeight > e.target.offsetHeight) {
            e.target.scrollTop = e.target.scrollHeight // 在末尾输入时，滚动到底部，防止被遮挡
        }
        // const value = cutValue(this.input.value || e.target.value, maxLength)
        // #QDJCJS-7720设置maxLength后搜狗输入法录入中文过程中，当录入快到达最大长度时，会删已经录入的文本(去掉cutValue方法之后，录入过程字节数可能会超过maxLength,但最终结果保持不变)
        // const value = this.input.value || limitValue
        if (propsValue === undefined || isInputChineseRef.current || composition === 'end') {
            setValue(limitValue ?? '')
        }

        if (isInputChineseRef.current) {
            return
        }
        antd ? onChange?.(e) : onChange?.(limitValue, e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, valueChinese: InputWithDefaultProps['value'], composition?: 'start' | 'end') => {
        const now = new Date().getTime()
        if (now - lastScrollCallRef.current < debounceDelay) return
        lastScrollCallRef.current = now
        // const value = cutValue(this.input.value || e.target.value, maxLength)
        // #QDJCJS-7720设置maxLength后搜狗输入法录入中文过程中，当录入快到达最大长度时，会删已经录入的文本(去掉cutValue方法之后，录入过程字节数可能会超过maxLength,但最终结果保持不变)
        const newValue = valueChinese || inputRef.current?.value || e.target.value
        if (propsValue === undefined || isInputChineseRef.current || composition === 'end') {
            setValue(newValue ?? '')
        }
        if (isInputChineseRef.current) {
            return
        }
        antd ? onChange?.(e) : onChange?.(newValue, e)
    }

    // 输入中文处理，文字合成输入过程中不再触发change
    const handleComposition = (e: React.CompositionEvent<HTMLInputElement>, composition: 'start' | 'end') => {
        if (composition === 'start') {
            isInputChineseRef.current = true
            onCompositionStart?.(e)
        } else if (composition === 'end') {
            const newValue = inputRef.current?.value || (e as any).target?.value
            // 输入法切换过程中ref 会变为null
            isInputChineseRef.current = false
            onCompositionEnd?.(e)
            if (type === 'search') {
                handleSearchChange(e as unknown as React.ChangeEvent<HTMLInputElement>, newValue, composition)
            } else if (type === 'textarea' || componentClass === 'textarea') {
                handleTextareaChange(e as unknown as React.ChangeEvent<HTMLInputElement>, newValue, composition)
            } else {
                handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>, newValue, composition)
            }
        }
    }

    const clearValue = (e?: React.MouseEvent<HTMLDivElement> | any) => {
        e?.stopPropagation?.()
        if (disabled) return
        setValue('')
        if (eRef.current && eRef.current.target) {
            eRef.current.target.value = ''
        }
        antd ? onChange?.(eRef.current) : onChange?.('', eRef.current)
        inputRef.current?.focus()
        // fix: 代码与文档不一致，input框清空时需要调用onSearch方法，参数为空
        if (type === 'search') onSearch?.('', e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const {type, componentClass: Component} = props;
        let _e = Object.assign({}, e)
        eRef.current = _e
        if (e.keyCode === 13 && !(type === 'textarea' || Component === 'textarea')) {
            onPressEnter?.(e)
            if (type === 'search') onSearch?.(inputRef.current.value, e)
        }
        // QDKJ-85584 react18改造后，阻止冒泡导致该场景下Table中使用上下键失效，对接人：李明阳
        if (!ignoreStopKey) stopKeyEventBubbles(e) // 阻止LEFT, RIGHT, UP, DOWN, ENTER, ESC等按键事件冒泡
        onKeyDown?.(e)
    }

    const handleSearch = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!disabled && !loading) onSearch?.(value as any, e)
    }

    const trimString = (value: string | number, trim: InputProps['trim']) => {
        if (trim && typeof value === 'string') {
            value = trim === 'left' ? value.trimStart() : trim === 'right' ? value.trimEnd() : value.trim()
        }
        return value
    }

    const focus = () => {
        inputRef.current?.focus()
    }

    const blur = () => {
        inputRef.current?.blur()
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (readOnly) return
        let trimValue = value ?? ''
        if (trim && typeof value === 'string') {
            trimValue = trimString(value, trim)
        }
        let _e = Object.assign({}, e)
        eRef.current = _e
        setFocused(false)
        setValue(trimValue)
        let callbackData: any = antd ? e : trimValue
        if (trimValue !== value) {
            antd ? onChange?.(e) : onChange?.(trimValue, e)
        }
        if (onBlur) {
            if (allowClear && clickClearBtnRef.current) {
                clickClearBtnRef.current = false
                onBlur?.(callbackData, _e, true)
            } else if (clickSearchBtnRef.current) {
                clickSearchBtnRef.current = false
                onBlur?.(callbackData, _e, true)
            } else {
                onBlur?.(callbackData, _e)
            }
        }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (readOnly) return
        if ([type, componentClass].includes('textarea') && e.target.selectionStart === 0) { // QDJCJS-23488 修复textarea在autoFocus时光标设置到结束位置
            e.target.setSelectionRange((value + '').length, (value + '').length)
        }
        if (focusSelect) {
            e.currentTarget.select?.()
            inputRef.current?.select?.()
        }
        let callbackData: any = antd ? e : value
        setFocused(!disabled)
        onFocus?.(callbackData, e)
    }

    const handleClick: React.MouseEventHandler<HTMLInputElement> = (e) => {
        onClick?.(e)
    }

    const onClearBtnMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        clickClearBtnRef.current = true
    }

    const onSearchBtnMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        clickSearchBtnRef.current = true
    }

    /**
     * 字数限制文本
     */
    const renderLimitText = ({currentLength, maxLength}: {currentLength: number; maxLength?: number}) => {
        const {type, componentClass, showMaxLabel, clsPrefix} = propsWithDefaults
        const isTextarea = type === 'textarea' || componentClass === 'textarea'
        if (
            (isTextarea || type === 'text') &&
            (showMaxLabel ?? false) &&
            maxLength !== undefined
        ) {
            const limitPrefix = isTextarea ? textareaPrefix : clsPrefix
            const currentCls = classnames(`${limitPrefix}-limit-current`, {
                [`${limitPrefix}-limit-current-warning`]: maxLength && currentLength > maxLength
            })
            return (
                <span className={`${limitPrefix}-limit`}>
                    <span className={currentCls}>{currentLength}</span>
                    {`/${maxLength}`}
                </span>
            )
        }
    }

    const renderBrowser = (value?: string | number, Component?: any, inputType?: string) => {
        const { clsPrefix, browserClassName, prefix, suffix, browserStyle, type} = propsWithDefaults
        let title = String(value)
        if (typeof prefix === 'string') title = prefix + title
        if (typeof suffix === 'string') title = title + suffix

        // 判断是否需要显示为可点击状态
        const isClickable = onLinkClick && value
        const handleTriggerClick = () => {
            if (isClickable && onLinkClick) {
                onLinkClick(value)
            }
        }

        return (
            <div className={classnames(`${componentPrefix}-component-browser`)}>
                <div className={classnames(`${clsPrefix}-browser`, {
                    [`${componentPrefix}-component-browser-border-bottom`]: bordered === 'bottom',
                    [`${browserClassName}`]: browserClassName,
                    [`${clsPrefix}-textarea-browser`]: type === 'textarea' || (inputType === 'input' && Component && Component === 'textarea'),
                    [`${clsPrefix}-browser-clickable`]: isClickable
                })} fieldid={fieldid ? fieldid : undefined} style={browserStyle} title={inputType === 'password' && !passwordVisible ? undefined : title} onClick={handleTriggerClick}
                >
                    {
                        value ? (
                            <>
                                {prefix}
                                {inputType === 'password' && !passwordVisible ? '.'.repeat(String(value).length) : value}
                                {suffix}
                            </>
                        ) : '--'
                    }
                </div>
            </div>
        )
    }

    // TODO: renderInput，renderSearch, renderPassword应合并为同一实现
    const renderInput = () => {
        const { style, componentClass: Component, type, debounceDelay, value: propsValue, maxLength, innerStyle, trigger, passwordVisible, nid, uitype, ...others } = propsWithDefaults
        let CurrentComp = Component
        // input[type="file"] 不应该有类名 .form-control.
        const currentValue = (isInputChineseRef.current || propsValue === undefined) ? value : propsValue
        let classes: Record<string, any> = {}
        // 单独的 md / xs等size
        if (size) {
            classes[size] = true
        }
        let isTextarea = false

        // 浏览态
        if (browser) {
            return renderBrowser(currentValue, Component, 'input')
        }

        if (type === 'textarea' || Component === 'textarea') {
            isTextarea = true
            CurrentComp = TextArea
        }
        let classNames
        if (type !== 'file') {
            classNames = classnames(clsPrefix, classes)
        }
        if (prefix || suffix) classNames += ` ${clsPrefix}-prefix-suffix`

        // 加判断，是否有 前后缀，是否加 wrapper
        let focusClassName = focused ? `${clsPrefix}-affix-focus` : ''
        let adapterNid = getNid(propsWithDefaults) // 适配nid、uitype

        // 判断是否需要显示为可点击状态（readOnly模式下）
        const isClickable = readOnly && onLinkClick && currentValue
        const handleTriggerClick = () => onLinkClick && onLinkClick(currentValue)

        if (allowClear || suffix || prefix || (showMaxLabel ?? false)) {
            if (innerClassName) classNames += ' ' + innerClassName
            return (
                <div
                    id={id ? id + '_input' : undefined}
                    fieldid={fieldid ? fieldid + '_input' : undefined}
                    className={classnames(
                        `${clsPrefix}-affix-wrapper ${clsPrefix}-affix-wrapper-${size}`,
                        isTextarea ? textareaPrefix + '-affix-wrapper' : '',
                        focusClassName,
                        className,
                        {
                            ...setComponentClass({clsPrefix, bordered, align, requiredStyle}),
                            [`${clsPrefix}-affix-wrapper-disabled`]: others.disabled,
                            [`${clsPrefix}-affix-wrapper-readonly`]: readOnly,
                            [`${clsPrefix}-affix-wrapper-clickable`]: isClickable,
                            [`${clsPrefix}-close`]: allowClear,
                            [`${clsPrefix}-showMaxLabel`]: showMaxLabel,
                            [`${clsPrefix}-autosize`]: isTextarea && !!propsAutoSize,
                        }
                    )}
                    style={style}
                    onClick={isClickable ? handleTriggerClick : handleClick}
                    {...adapterNid}
                >
                    {prefix && (
                        <span
                            id={id ? id + '_prefix' : undefined}
                            fieldid={fieldid ? fieldid + '_prefix' : undefined}
                            className={`${clsPrefix}-simple-prefix`}
                        >
                            {prefix}
                        </span>
                    )}
                    <CurrentComp
                        {...omit(others, ['antd', 'onClick', 'trim'])}
                        {...(isTextarea
                            ? {
                                prefixCls: textareaPrefix /* textarea使用rc，需要加前缀 */,
                                showMaxLabel: showMaxLabel ?? false,
                                autoSize,
                                onPressEnter: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                    onPressEnter?.(e as any)
                                    if (type === 'search') onSearch?.(inputRef.current.value, e as any)
                                }
                            }
                            : {type})}
                        id={id}
                        fieldid={fieldid}
                        // type={type}
                        style={innerStyle}
                        ref={inputRef}
                        autoFocus={autoFocus}
                        placeholder={getChildrenText(placeholder).join('')}
                        value={currentValue ?? ''}
                        title={title || currentValue || getChildrenText(placeholder).join('') || ''}
                        readOnly={readOnly}
                        tabIndex={readOnly ? -1 : undefined}
                        onCompositionStart={(e: React.CompositionEvent<HTMLInputElement>) => {
                            handleComposition(e, 'start')
                        }}
                        onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => {
                            handleComposition(e, 'end')
                        }}
                        onChange={isTextarea ? handleTextareaChange : handleChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onKeyDown={handleKeyDown}
                        className={classnames(classNames)}
                        maxLength={maxLength}
                        maxlength={isTextarea && !allowInputOverMax ? maxLength : undefined}
                    />
                    {
                        // textarea最长文本数字
                        renderLimitText({
                            // spread操作符用于处理表情等合成字符slice后错误或乱码等问题，如：一家三口'👨👨👨'.slice(0, 2) === '👨'
                            currentLength: (String(currentValue ?? '')).length,
                            maxLength
                        })
                    }
                    {allowClear && (currentValue || currentValue === 0) && !(readOnly || others.disabled) && (
                        <div
                            className={`${clsPrefix}-suffix has-close`}
                            id={id ? id + '_clear' : undefined}
                            fieldid={fieldid ? fieldid + '_clear' : undefined}
                            onMouseDown={onClearBtnMouseDown}
                            onClick={clearValue}
                            style={
                                isTextarea && suffix
                                    ? { right: `${suffixWidth + 13}px` } // 13px = 5px(原始right) + 8px(间距)
                                    : undefined
                            }
                        >
                            <Icon type='uf-close-c' />
                        </div>
                    )}
                    {suffix && (
                        <span
                            className={`${clsPrefix}-simple-suffix`}
                            id={id ? id + '_suffix' : undefined}
                            fieldid={fieldid ? fieldid + '_suffix' : undefined}
                            ref={isTextarea && allowClear ? suffixRef : undefined}
                        >
                            {suffix}
                        </span>
                    )}
                </div>
            )
        } else {
            if (className) classNames += ' ' + className
            return (
                <CurrentComp
                    style={style} // TODO: 此处貌似应使用innerStyle
                    {...omit(others, ['antd', 'trim'])}
                    {...(isTextarea ? {
                        prefixCls: textareaPrefix /* textarea使用rc，需要加前缀 */,
                        autoSize,
                        onPressEnter: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            onPressEnter?.(e as any)
                            if (type === 'search') onSearch?.(inputRef.current.value, e as any)
                        }
                    } : {type})}
                    id={id}
                    fieldid={fieldid}
                    // type={type}
                    ref={inputRef}
                    autoFocus={autoFocus}
                    placeholder={getChildrenText(placeholder).join('')}
                    value={currentValue ?? ''}
                    title={title || currentValue || getChildrenText(placeholder).join('') || ''}
                    readOnly={readOnly}
                    tabIndex={readOnly ? -1 : undefined}
                    onCompositionStart={(e: React.CompositionEvent<HTMLInputElement>) => {
                        handleComposition(e, 'start')
                    }}
                    onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => {
                        handleComposition(e, 'end')
                    }}
                    onChange={isTextarea ? handleTextareaChange : handleChange}
                    onBlur={handleBlur}
                    onClick={isClickable ? handleTriggerClick : handleClick}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    className={classnames(classNames, {
                        [`${clsPrefix}-${size}`]: !!size && !isTextarea,
                        [`${textareaPrefix}-no-limit`]: isTextarea,
                        ...setComponentClass({clsPrefix, bordered, align, requiredStyle}),
                        [`${clsPrefix}-readonly`]: readOnly,
                        [`${clsPrefix}-clickable`]: isClickable
                    })}
                    maxLength={maxLength}
                    maxlength={isTextarea && !allowInputOverMax ? maxLength : undefined}
                    {...adapterNid}
                />
            )
        }
    }

    const renderSearch = () => {
        const { componentClass: Component, type, clsPrefix, debounceDelay, value: propsValue, style, loading, innerStyle,
            trigger, icon, enterButton = false, passwordVisible, showMaxLabel, nid, uitype, ...others
        } = propsWithDefaults
        // input[type="file"] 不应该有类名 .form-control.
        const currentValue = (isInputChineseRef.current || propsValue === undefined) ? value : propsValue
        let classes: Record<string, boolean> = {}
        if (size) {
            classes[size] = true
        }
        // 浏览态
        if (browser) {
            return renderBrowser(currentValue, null, 'search')
        }
        let focusClassName = focused ? `${clsPrefix}-affix-focus` : ''

        const searchIcon = icon || <Icon type='uf-search-light-2' />
        const pureIcon = icon
            ? React.cloneElement(icon, {
                onClick: () => {} // 图标的click上移到button执行，增加触发区域
            })
            : ''
        let iconProps: Record<string, string> = {}
        if (id) iconProps.id = `${id}-search`

        let button: React.ReactNode
        const enterButtonAsElement = (enterButton || {}) as React.ReactElement

        if (!enterButton && !loading) {
            button = React.cloneElement(searchIcon, {
                ...iconProps,
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    searchIcon?.props?.onClick?.(e)
                    handleSearch(e)
                }
            })
        } else if (
            enterButtonAsElement?.type === 'button' ||
            (enterButtonAsElement?.type as any)?.defaultProps?.clsPrefix === `${componentPrefix}-button`
        ) {
            button = React.cloneElement(enterButtonAsElement, {
                colors: 'primary',
                ...iconProps,
                key: 'enterButton',
                loading,
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    icon?.props?.onClick?.(e)
                    enterButtonAsElement?.props?.onClick?.(e)
                    handleSearch(e)
                },
                children: (
                    <>
                        {enterButtonAsElement?.props?.children ?? ''}
                        {pureIcon}
                    </>
                )
            })
        } else {
            button = (
                <Button
                    colors='primary'
                    {...iconProps}
                    key='enterButton'
                    className={classnames(`${clsPrefix}-enterbutton`, iconProps?.className)}
                    loading={loading}
                    onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        icon?.props?.onClick?.(e)
                        handleSearch(e)
                    }}
                >
                    {enterButton}
                    {pureIcon}
                </Button>
            )
        }
        let adapterNid = getNid(propsWithDefaults) // 适配nid、uitype
        // 判断是否需要显示为可点击状态（readOnly模式下）
        const isClickable = readOnly && onLinkClick && currentValue
        const handleTriggerClick = () => onLinkClick && onLinkClick(currentValue)
        return (
            <div
                id={id ? id + '_input' : undefined}
                fieldid={fieldid ? fieldid + '_input' : undefined}
                className={classnames(`${clsPrefix}-search`, `${clsPrefix}-affix-wrapper`, focusClassName, className, {
                    [`${clsPrefix}-affix-wrapper-${size}`]: !!size,
                    ...setComponentClass({clsPrefix, bordered, align, requiredStyle}),
                    [`${clsPrefix}-with-button`]: !!enterButton || !!loading,
                    [`${clsPrefix}-affix-wrapper-readonly`]: readOnly,
                    [`${clsPrefix}-affix-wrapper-disabled`]: others.disabled,
                    [`${clsPrefix}-affix-wrapper-clickable`]: isClickable,
                    [`${clsPrefix}-close`]: allowClear
                })}
                onClick={isClickable ? handleTriggerClick : handleClick}
                style={style}
                {...adapterNid}
            >
                <Component
                    style={innerStyle}
                    {...omit(others, ['antd', 'onClick', 'trim'])}
                    id={id}
                    fieldid={fieldid}
                    type={type}
                    readOnly={readOnly}
                    tabIndex={readOnly ? -1 : undefined}
                    ref={inputRef}
                    autoFocus={autoFocus}
                    onChange={handleSearchChange}
                    placeholder={getChildrenText(placeholder).join('')}
                    value={currentValue ?? ''}
                    title={title || currentValue || getChildrenText(placeholder).join('') || ''}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={(e: React.CompositionEvent<HTMLInputElement>) => {
                        handleComposition(e, 'start')
                    }}
                    onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => {
                        handleComposition(e, 'end')
                    }}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    className={classnames(clsPrefix, classes, innerClassName)}
                    maxLength={maxLength}
                />
                {allowClear && (currentValue || currentValue === 0) && !(readOnly || others.disabled) ? (
                    <div
                        className={`${clsPrefix}-suffix has-close`}
                        id={id ? id + '_clear' : undefined}
                        fieldid={fieldid ? fieldid + '_clear' : undefined}
                        onMouseDown={onClearBtnMouseDown}
                        onClick={clearValue}
                    >
                        <Icon type='uf-close-c' />
                    </div>
                ) : (
                    ''
                )}
                <div className={`${clsPrefix}-suffix`} fieldid={fieldid ? fieldid + '_search' : undefined} onMouseDown={onSearchBtnMouseDown}>
                    {button}
                </div>
            </div>
        )
    }

    const getIcon = (prefixCls: string, icon: React.ReactNode) => {
        const { disabled, onVisibleChange} = propsWithDefaults
        const triggerVisibleChange = () => {
            if (disabled) {
                return
            }
            setPasswordVisible(!passwordVisible)
            removePasswordValue()
            onVisibleChange?.(!passwordVisible)
        }
        const ActiconMap: Record<string, string> = {
            click: 'onClick',
            hover: 'onMouseOver'
        }
        const iconTrigger = ActiconMap[trigger] || ''
        const iconProps = {
            [iconTrigger]: triggerVisibleChange,
            className: `${prefixCls}-icon`,
            onMouseDown: (e: MouseEvent) => {
                e.preventDefault()
            },
            onMouseUp: (e: MouseEvent) => {
                e.preventDefault()
            }
        }

        return React.cloneElement(React.isValidElement(icon) ? icon : <span>{icon}</span>, iconProps)
    }

    const renderPassword = () => {
        const { componentClass: Component, type, clsPrefix, debounceDelay, value: propsValue, maxLength, innerStyle, style,
            trigger, showMaxLabel, nid, uitype, ...others
        } = propsWithDefaults
        const currentValue = propsValue || value

        // 浏览态
        if (browser) {
            return renderBrowser(currentValue, Component, 'password')
        }
        let classes: Record<string, boolean> = {}
        if (size) {
            classes[size] = true
        }
        let focusClassName = focused ? `${clsPrefix}-affix-focus` : ''
        const toggleVisibleIcon = iconRender(passwordVisible)
        let adapterNid = getNid(propsWithDefaults) // 适配nid、uitype

        const passwordInput = (hasIcons = true) => (
            <Component
                style={hasIcons ? innerStyle : style}
                {...omit(others, ['antd', 'onClick', 'passwordVisible', 'trim'])}
                id={id}
                fieldid={fieldid}
                type={passwordVisible ? 'text' : 'password'}
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : undefined}
                ref={inputRef}
                autoFocus={autoFocus}
                onChange={handleChange}
                placeholder={getChildrenText(placeholder).join('')}
                value={currentValue ?? ''}
                title={title || (passwordVisible && currentValue) || (!currentValue && getChildrenText(placeholder).join('')) || ''}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={handleFocus}
                className={classnames(clsPrefix, classes, innerClassName, {[`${clsPrefix}-${size}`]: !!size && !hasIcons, ...(hasIcons ? {} : setComponentClass({clsPrefix, bordered, align, requiredStyle}))})}
                maxLength={maxLength}
                {...(hasIcons === false ? adapterNid : {})}
            />
        )
        // 判断是否需要显示为可点击状态（readOnly模式下）
        const isClickable = readOnly && onLinkClick && currentValue
        const handleTriggerClick = () => onLinkClick && onLinkClick(currentValue)
        if (allowClear || visibilityToggle || suffix || prefix) {
            return (
                <div
                    id={id ? id + '_input' : undefined}
                    fieldid={fieldid ? fieldid + '_input' : undefined}
                    className={classnames(
                        `${clsPrefix}-password`,
                        `${clsPrefix}-affix-wrapper`,
                        focusClassName,
                        className,
                        {
                            [`${clsPrefix}-affix-wrapper-${size}`]: !!size,
                            ...setComponentClass({clsPrefix, bordered, align, requiredStyle}),
                            [`${clsPrefix}-affix-wrapper-disabled`]: others.disabled,
                            [`${clsPrefix}-affix-wrapper-readonly`]: readOnly,
                            [`${clsPrefix}-affix-wrapper-clickable`]: isClickable,
                            [`${clsPrefix}-close`]: allowClear
                        }
                    )}
                    onClick={isClickable ? handleTriggerClick : handleClick}
                    style={style}
                    {...adapterNid}
                >
                    {prefix && (
                        <span
                            id={id ? id + '_prefix' : undefined}
                            fieldid={fieldid ? fieldid + '_prefix' : undefined}
                            className={`${clsPrefix}-simple-prefix`}
                        >
                            {prefix}
                        </span>
                    )}
                    {passwordInput()}
                    {allowClear && (currentValue || currentValue === 0) && !(readOnly || others.disabled) ? (
                        <div
                            className={`${clsPrefix}-suffix has-close`}
                            id={id ? id + '_clear' : undefined}
                            fieldid={fieldid ? fieldid + '_clear' : undefined}
                            onMouseDown={onClearBtnMouseDown}
                            onClick={clearValue}
                        >
                            <Icon type='uf-close-c' />
                        </div>
                    ) : (
                        ''
                    )}
                    {visibilityToggle && toggleVisibleIcon && (
                        <div className={`${clsPrefix}-suffix`} fieldid={fieldid ? fieldid + '_password' : undefined}>
                            {getIcon(`${clsPrefix}-password`, toggleVisibleIcon)}
                        </div>
                    )}
                    {/* 兼容4.3.2以下版本用户通过suffix生成小眼睛的方案 */}
                    {suffix && (
                        <span
                            className={`${clsPrefix}-simple-suffix`}
                            id={id ? id + '_suffix' : undefined}
                            fieldid={fieldid ? fieldid + '_suffix' : undefined}
                        >
                            {suffix}
                        </span>
                    )}
                </div>
            )
        } else {
            return passwordInput(false)
        }
    }

    // Expose methods via ref
    useImperativeHandle(ref, () => {
        // 创建一个保护wrapper，防止外部监听器在中文输入期间干扰 QDJCJS-30261
        const createProtectedInput = () => {
            if (!inputRef.current) return null

            const originalInput = inputRef.current
            // 创建代理对象，完全透传原始input的所有属性和方法
            return new Proxy(originalInput, {
                get(target, prop) {
                    // 特殊处理 addEventListener 以保护中文输入
                    if (prop === 'addEventListener') {
                        return function(type: string, listener: any, options?: any) {
                            if (type === 'input') {
                                const protectedListener = (e: Event) => {
                                    // 在中文输入期间不触发外部监听器
                                    if (!isInputChineseRef.current) {
                                        listener(e)
                                    }
                                }
                                return target.addEventListener(type, protectedListener, options)
                            }
                            return target.addEventListener(type, listener, options)
                        }
                    }

                    // 确保方法正确绑定到原始对象
                    const value = target[prop]
                    return typeof value === 'function' ? value.bind(target) : value
                },
                set(target, prop, value) {
                    target[prop] = value
                    return true
                }
            })
        }

        return {
            focus,
            blur,
            clearValue,
            input: createProtectedInput(),
            isComposing: () => isInputChineseRef.current
        }
    }, [focus, blur, clearValue])

    // Render
    if (type === 'search') {
        return renderSearch()
    } else if (type === 'password') {
        return renderPassword()
    }

    return renderInput()
})

export default WebUI({ name: "input", defaultProps })(Input);
